import { ReactNode, useState, useEffect, useRef } from 'react';
import { useAccordionGroupSignals } from '../AccordionGroupContext';

/** Chevron SVG matching the design (rotates when open). */
const Chevron = () => (
  <svg className="chev" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

interface AccProps {
  /** Section title (renders in serif red). */
  title: ReactNode;
  /**
   * Compact summary preview shown in the header (e.g. "Jane Doe", "24 × 36 in").
   * If empty/null/undefined, the design's italic "empty" placeholder may be
   * shown via {@link emptyPlaceholder}.
   */
  summary?: ReactNode;
  /** Italic placeholder shown when {@link summary} is empty. */
  emptyPlaceholder?: ReactNode;
  /** Optional status pill ('ok' | 'req' | 'opt'). */
  status?: { kind: 'ok' | 'req' | 'opt'; label: ReactNode };
  /** Defaults to closed; controlled mode possible via {@link expanded}. */
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (open: boolean) => void;
  children: ReactNode;
  id?: string;
}

/**
 * Hi‑Fi accordion (`.acc` / `.acc-head` / `.acc-body`). Subscribes to the
 * shared {@link AccordionGroupContext} signals so the page's "Expand all /
 * Collapse all" toolbar can drive every accordion in one click.
 */
export const Acc = ({
  title,
  summary,
  emptyPlaceholder,
  status,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  children,
  id,
}: AccProps) => {
  const isControlled = controlledExpanded !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultExpanded);
  const open = isControlled ? controlledExpanded : internalOpen;

  const groupSignals = useAccordionGroupSignals();
  const lastExpandRef = useRef(groupSignals?.expandSignal ?? 0);
  const lastCollapseRef = useRef(groupSignals?.collapseSignal ?? 0);

  useEffect(() => {
    if (!groupSignals || isControlled) return;
    if (groupSignals.expandSignal !== lastExpandRef.current) {
      lastExpandRef.current = groupSignals.expandSignal;
      setInternalOpen(true); // eslint-disable-line react-hooks/set-state-in-effect -- broadcast subscriber
    }
    if (groupSignals.collapseSignal !== lastCollapseRef.current) {
      lastCollapseRef.current = groupSignals.collapseSignal;
      setInternalOpen(false);
    }
  }, [groupSignals, isControlled]);

  const toggle = () => {
    const next = !open;
    if (!isControlled) setInternalOpen(next);
    onExpandedChange?.(next);
  };

  const summaryEmpty = !summary;

  return (
    <div className={`acc${open ? ' open' : ''}`} id={id} data-acc>
      <button type="button" className="acc-head" onClick={toggle} aria-expanded={open}>
        <span className="title">{title}</span>
        {summaryEmpty ? (
          emptyPlaceholder && <span className="summary empty">{emptyPlaceholder}</span>
        ) : (
          <span className="summary">{summary}</span>
        )}
        {status && <span className={`status ${status.kind}`}>{status.label}</span>}
        <Chevron />
      </button>
      <div className="acc-body">{children}</div>
    </div>
  );
};

interface AccToolbarProps {
  onExpandAll: () => void;
  onCollapseAll: () => void;
}

export const AccToolbar = ({ onExpandAll, onCollapseAll }: AccToolbarProps) => (
  <div className="acc-toolbar">
    <button type="button" className="link-btn" onClick={onExpandAll}>
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 5v14M5 12h14" />
      </svg>
      Expand all
    </button>
    <span className="sep">·</span>
    <button type="button" className="link-btn" onClick={onCollapseAll}>
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 12h14" />
      </svg>
      Collapse all
    </button>
  </div>
);
